/**
 * shared/components/SelectionToolbar.jsx + useSelection
 * ------------------------------------------------------
 * Universal multi-select engine. Drop into any list page (TeamPropertiesPage,
 * /admin/properties, dealer browse) to enable bulk actions.
 *
 * USAGE
 * -----
 *   const sel = useSelection();
 *   // Each item card:
 *   <button onClick={() => sel.toggle(p.id)} className={sel.has(p.id) ? "ring-2 ring-indigo-500" : ""}>
 *     {sel.mode && <Check ... checked={sel.has(p.id)} />}
 *     ... card body ...
 *   </button>
 *   // Toggle multi-select mode:
 *   <button onClick={() => sel.setMode(!sel.mode)}>Select</button>
 *   // Sticky bottom bar:
 *   <SelectionToolbar
 *     sel={sel}
 *     onShortlist={() => openShortlistSheet(sel.ids)}
 *     onShare={() => copyAllLinks(sel.ids)}
 *   />
 *
 * v2.71.52 — Initial.
 */

import { useCallback, useMemo, useState } from "react";
import React from "react";
import { Button } from "../../components/ui/button";
import { X, ListChecks, Share2, MessageCircle, CheckCheck } from "lucide-react";

export function useSelection() {
  const [mode, setMode] = useState(false);
  const [ids, setIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const has = useCallback((id) => ids.has(id), [ids]);

  const clear = useCallback(() => { setIds(new Set()); setMode(false); }, []);

  const selectAll = useCallback((allIds) => {
    setIds(new Set(allIds));
  }, []);

  const count = ids.size;
  const idList = useMemo(() => Array.from(ids), [ids]);

  return { mode, setMode, ids, idList, count, toggle, has, clear, selectAll };
}

export default function SelectionToolbar({
  sel,
  onShortlist = null,
  onShare = null,
  onWhatsapp = null,
  onCustom = null,           // optional custom action: { label, icon, onClick }
  testidPrefix = "selection",
}) {
  if (!sel.mode && sel.count === 0) return null;

  return (
    <div
      data-testid={`${testidPrefix}-toolbar`}
      className="fixed left-0 right-0 bottom-16 sm:bottom-4 z-40 px-3 pointer-events-none"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="pointer-events-auto mx-auto max-w-md bg-slate-900 text-white rounded-2xl shadow-2xl px-3 py-2.5 flex items-center gap-2">
        <button
          data-testid={`${testidPrefix}-clear`}
          onClick={sel.clear}
          className="shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          aria-label="Clear selection"
        >
          <X size={14} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold">
            {sel.count} <span className="font-normal text-white/70">Selected</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onShortlist && (
            <Button
              data-testid={`${testidPrefix}-shortlist`}
              size="sm"
              onClick={() => onShortlist(sel.idList)}
              disabled={sel.count === 0}
              className="bg-indigo-500 hover:bg-indigo-600 h-8 px-2.5 text-[11.5px] font-semibold"
            >
              <ListChecks size={13} className="mr-1" /> Shortlist
            </Button>
          )}
          {onShare && (
            <Button
              data-testid={`${testidPrefix}-share`}
              size="sm"
              onClick={() => onShare(sel.idList)}
              disabled={sel.count === 0}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 h-8 px-2.5 text-[11.5px] font-semibold bg-transparent"
            >
              <Share2 size={13} className="mr-1" /> Share
            </Button>
          )}
          {onWhatsapp && (
            <Button
              data-testid={`${testidPrefix}-wa`}
              size="sm"
              onClick={() => onWhatsapp(sel.idList)}
              disabled={sel.count === 0}
              className="bg-[#25D366] hover:bg-[#1ebe5d] h-8 px-2.5 text-[11.5px] font-semibold"
            >
              <MessageCircle size={13} />
            </Button>
          )}
          {onCustom && (
            <Button
              data-testid={`${testidPrefix}-custom`}
              size="sm"
              onClick={() => onCustom.onClick(sel.idList)}
              disabled={sel.count === 0}
              className="h-8 px-2.5 text-[11.5px] font-semibold"
            >
              {onCustom.icon ? React.createElement(onCustom.icon, { size: 13, className: "mr-1" }) : <CheckCheck size={13} className="mr-1" />}
              {onCustom.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
